// Jednotky rozsahu produktu podle typu vazby – jediný zdroj pravdy.
//
// Oborový standard:
// - Stránkované publikace (V1, V2–V8) se specifikují v POČTU STRAN (vč. obálky).
//   U V1 je násobek 4 (1 přeložený list / složka = 4 strany), u lepených násobek 2.
// - Bloky, zápisníky a kroužkové bloky/kalendáře se specifikují v POČTU LISTŮ
//   (1 list = 2 strany, líc a rub).
// - Složka (přeložený list) je výrobní pojem – zákazníkovi se nenabízí jako
//   jednotka, ale výroba ji potřebuje vidět v technických listech.

/** České skloňování podle počtu: sklon(3, ['strana', 'strany', 'stran']) → 'strany'. */
function sklon(n, tvary) {
  if (n === 1) return tvary[0];
  if (n >= 2 && n <= 4) return tvary[1];
  return tvary[2];
}

export function jeKrouzkova(typ) {
  return typeof typ === 'string' && typ.startsWith('krouzek');
}

export function jeBlokova(typ) {
  return typ === 'blok' || typ === 'blokPrebal';
}

export function jeV1(typ) {
  return typ === 'V1-strojni' || typ === 'V1-rucni';
}

/** Metadata jednotky pro daný typ vazby (label, min/step, násobek, slovo). */
export function jednotkyVazby(typ) {
  if (!typ || typ === '0') {
    return { unit: null, slovo: 'stran', label: 'Počet stran', min: 4, step: 4, nasobek: null };
  }
  if (jeBlokova(typ)) {
    return { unit: 'listy', slovo: 'listů', label: 'Počet listů vnitřního bloku', min: 1, step: 1, nasobek: null };
  }
  if (jeKrouzkova(typ)) {
    return { unit: 'listy', slovo: 'listů', label: 'Počet listů (vč. obálkových)', min: 1, step: 1, nasobek: null };
  }
  if (jeV1(typ)) {
    return { unit: 'strany', slovo: 'stran', label: 'Počet stran (vč. obálky)', min: 4, step: 4, nasobek: 4 };
  }
  // Ostatní stránkové vazby (V2-PUR, V3, V4, V5, V6, V8, V2V8…)
  return { unit: 'strany', slovo: 'stran', label: 'Počet stran (vč. obálky)', min: 4, step: 2, nasobek: 2 };
}

/**
 * Živý přepočet jednotek pod inputem – vysvětlí zadané číslo v „druhé" jednotce.
 * @param {string} typ            typ vazby
 * @param {number} hodnota        zadaný počet stran/listů
 * @param {object} volby          { jinaObalka, obalkaZada }
 * @returns {string} text helperu ('' pokud není co říct)
 */
export function prepocetJednotek(typ, hodnota, volby = {}) {
  const n = Math.max(0, Math.round(+hodnota || 0));
  if (!typ || typ === '0' || !n) return '';

  if (jeBlokova(typ)) {
    const prebal = typ === 'blokPrebal' ? ' · přebal se počítá zvlášť' : '';
    return `${n} identických listů vnitřního bloku (1 list = 2 strany)${prebal}`;
  }

  if (jeKrouzkova(typ)) {
    const strany = n * 2;
    const listuSlovo = sklon(n, ['list', 'listy', 'listů']);
    if (volby.jinaObalka) {
      const obalkaListu = volby.obalkaZada === false ? 1 : 2;
      const vnitrnich = Math.max(0, n - obalkaListu);
      const obalkaText = obalkaListu === 2 ? 'obálka (přední + zadní list)' : 'obálka (jen přední list)';
      return `${n} ${listuSlovo} = ${obalkaText} + ${vnitrnich} vnitřních · 1 list = 2 strany (až ${strany} stran potisku)`;
    }
    return `${n} ${listuSlovo} · 1 list = 2 strany, líc a rub (až ${strany} stran potisku)`;
  }

  if (jeV1(typ)) {
    if (n % 4 !== 0) return '';
    const slozky = n / 4;
    const stranSlovo = sklon(n, ['strana', 'strany', 'stran']);
    const slozkyText = (k) => `${k} přeložen${k === 1 ? 'ý list (složka)' : k < 5 ? 'é listy (složky)' : 'ých listů (složek)'}`;
    if (volby.jinaObalka && slozky >= 2) {
      return `${n} ${stranSlovo} = obálka (4 str.) + ${slozkyText(slozky - 1)} vnitřku · 1 přeložený list (složka) = 4 strany`;
    }
    return `${n} ${stranSlovo} = ${slozkyText(slozky)} · 1 přeložený list = 4 strany`;
  }

  // Stránkové vazby: obálka = 4 strany (1 přeložený list), vnitřek 1 list = 2 strany
  if (n % 2 !== 0) return '';
  const stranSlovo2 = sklon(n, ['strana', 'strany', 'stran']);
  if (volby.jinaObalka && n > 4) {
    const listuVnitrku = (n - 4) / 2;
    return `${n} ${stranSlovo2} = obálka (4 str.) + ${listuVnitrku} ${sklon(listuVnitrku, ['list', 'listy', 'listů'])} vnitřku · 1 list = 2 strany`;
  }
  const listu2 = Math.ceil(n / 2);
  return `${n} ${stranSlovo2} = ${listu2} ${sklon(listu2, ['list', 'listy', 'listů'])} · 1 list = 2 strany`;
}

/**
 * Validace násobku pro stránkované vazby. Vrací '' (OK) nebo chybovou zprávu
 * s nejbližšími platnými hodnotami.
 */
export function validaceNasobku(typ, hodnota) {
  const meta = jednotkyVazby(typ);
  const n = Math.round(+hodnota || 0);
  if (!meta.nasobek || !n || n % meta.nasobek === 0) return '';
  const dolu = Math.max(meta.min, Math.floor(n / meta.nasobek) * meta.nasobek);
  const nahoru = Math.ceil(n / meta.nasobek) * meta.nasobek;
  const duvod = meta.nasobek === 4
    ? '1 přeložený list (složka) = 4 strany'
    : '1 list = 2 strany';
  return `Počet stran musí být násobek ${meta.nasobek} (${duvod}). Nejbližší platné hodnoty: ${dolu === nahoru ? dolu : dolu + ' nebo ' + nahoru}.`;
}

/**
 * Rozsah produktu pro TECHNICKÉ LISTY – jednotka zákazníka + přepočet
 * na jednotku výroby (složky / strany potisku).
 * @param {object} vazba   result.vazba z kalkulace ({typ, pocetStran, jinaObalka, pocetListuObalka…})
 * @returns {string} např. „16 stran = 4 složky (přeložené listy)" nebo ''
 */
export function rozsahProVyrobu(vazba) {
  if (!vazba || !vazba.typ || vazba.typ === '0' || !vazba.pocetStran) return '';
  const n = vazba.pocetStran;

  if (jeBlokova(vazba.typ)) {
    return `${n} ${sklon(n, ['list', 'listy', 'listů'])} vnitřního bloku`;
  }
  if (jeKrouzkova(vazba.typ)) {
    return `${n} ${sklon(n, ['list', 'listy', 'listů'])} = až ${n * 2} stran potisku`;
  }
  if (jeV1(vazba.typ)) {
    const slozek = Math.ceil(n / 4);
    return `${n} ${sklon(n, ['strana', 'strany', 'stran'])} = ${slozek} ${sklon(slozek, ['složka', 'složky', 'složek'])} (přeložené listy)`;
  }
  const listuR = Math.ceil(n / 2);
  return `${n} ${sklon(n, ['strana', 'strany', 'stran'])} = ${listuR} ${sklon(listuR, ['list', 'listy', 'listů'])}`;
}
